import { useState, useEffect } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChartResponse, useGetApiChartCategory, useGetApiDashboardContrats } from '@/api';
import { getGetApiDashboardContratsQueryKey } from '@/api/dashboard/dashboard';
import ArbitrageSlider from '@/components/ArbitrageSlider/ArbitrageSlider';
import CardInProgress from '@/components/CardInProgress/CardInProgress';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import ContractGridCard from '@/components/ContractGridCard/ContractGridCard';
import DemoTag from '@/components/DemoTag/DemoTag';
import { dataDropDown } from '@/components/DropDownContrats/dataDropDown';
import DropDownContrats from '@/components/DropDownContrats/DropDownContrats';
import LineChart from '@/components/LineChart/LineChart';
import { dataButtonFiltre } from '@/components/LineChart/lineChartButtons';
import { Loader } from '@/components/Loader/loader';
import { TrendingValue } from '@/components/TrendingValue/TrendingValue';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTE } from '@/constants/route';
import { URLS } from '@/constants/urls';
import { TIMESPAN } from '@/enums/timespan';
import { useDemoContext } from '@/hooks/DemoContext';
import { useAuth } from '@/hooks/useAuth';
import useCancellationHandler from '@/hooks/useCancellationHandler';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalRenonciation from '@/modals/ContentModalRenonciation/ContentModalRenonciation';
import { performanceFilter } from '@/utils/functions/performanceFilter/performanceFilter';

import styles from './styles.module.css';

const capitalizeFirstLetter = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const getFamilyDisplayName = (famille: string) => {
  if (famille === 'ASSURANCEVIE') {
    return 'Assurance-vie';
  }
  if (famille === 'DEFISCALISATION') {
    return 'Défiscalisation';
  }
  return capitalizeFirstLetter(famille);
};

const computeDateOuverture = (selectedValue: string, dateGlobal: string, dateEncours?: string | null): string => {
  if (selectedValue === 'touslescontrats') return dateGlobal;
  if (dateEncours) return dateEncours.split('-').reverse().join('/');
  return '--/--/----';
};

const createFamilyFilter =
  (selectedValue: string) =>
  (family: { famille?: string | null }): boolean =>
    selectedValue.toLowerCase() === 'touslescontrats' || family.famille?.toLowerCase() === selectedValue.toLowerCase();

const CHART_ELIGIBLE_FAMILIES = new Set(['ASSURANCEVIE', 'RETRAITE', 'LIVRET', 'DEFISCALISATION']);
const NON_CHART_SELECTIONS = new Set(['immobilier', 'bourse']);

const shouldShowChart = (
  selectedValue: string,
  families?: { famille?: string | null; listContrat?: { enCours?: unknown[] | null } | null }[] | null
): boolean => {
  if (NON_CHART_SELECTIONS.has(selectedValue)) return false;
  if (!families?.some((contrat) => CHART_ELIGIBLE_FAMILIES.has(contrat?.famille ?? ''))) return false;
  return true;
};

const shouldHideTrendingValue = (
  selectedValue: string,
  families?: { famille?: string | null; listContrat?: { enCours?: unknown[] | null } | null }[] | null
): boolean =>
  selectedValue === 'touslescontrats' &&
  (families?.some(
    (f) => (f.famille === 'IMMOBILIER' || f.famille === 'BOURSE') && (f.listContrat?.enCours?.length ?? 0) > 0
  ) ??
    false);

export const Contracts: React.FC = () => {
  const { t } = useTranslation();
  const { windowWidth } = useScreenSize();
  const { user } = useAuth();
  const { isDemo } = useDemoContext();
  const navigate = useNavigate();
  const [lineChartTimespan, setLineChartTimespan] = useState<TIMESPAN>(TIMESPAN.allTime);
  const { data, isPending, isError } = useGetApiDashboardContrats();
  const [selectedValue, setSelectedValue] = useState<string>('touslescontrats');
  const { data: chartDetails, isLoading } = useGetApiChartCategory(
    selectedValue === 'touslescontrats' ? '' : selectedValue
  );
  const [performance, setPerformance] = useState<number | null>(0);
  const [plusMoinsValue, setPlusMoinsValue] = useState<number | null>(0);
  const { showCancelModal, selectedContract, getCancellationHandler, handleCloseCancelModal } =
    useCancellationHandler();
  const queryClient = useQueryClient();
  const [performanceFamily, setPerformanceFamily] = useState<number | null>(0);
  const [plusMoinsValueFamily, setPlusMoinsValueFamily] = useState<number | null>(0);

  const { filterPerformanceDashboard } = performanceFilter(data);
  const { filterPerformanceDashboard: filterPerformanceFamily } = performanceFilter(
    chartDetails as ChartResponse | undefined
  );

  const options = dataDropDown.map((option) => ({
    value: option.value,
    label: option.label,
    disabled: option.disabled,
    name: option.name,
    id: option.id,
  }));

  const handleDropdownChange = (value: string) => {
    setSelectedValue(value);
  };

  useEffect(() => {
    sessionStorage.removeItem('parcoursVersementData');
  }, []);

  useEffect(() => {
    filterPerformanceDashboard({ lineChartTimespan, setPerformance, setPlusMoinsValue });
    filterPerformanceFamily({
      lineChartTimespan,
      setPerformance: setPerformanceFamily,
      setPlusMoinsValue: setPlusMoinsValueFamily,
    });
  }, [selectedValue, lineChartTimespan, data, chartDetails]);

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'mes contrats',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'mes contrats',
        event_page: 'espace client - mes contrats',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [user?.id, user?.type, user?.success]);

  useEffect(() => {
    if (!isError) return;
    navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.DASHBOARD } });
  }, [isError, navigate]);

  if (isPending)
    return (
      <div>
        <Loader />
      </div>
    );

  if (isError) return null;

  const date = new Date();
  const dateOuvertureGlobal = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  const dateOuverture = computeDateOuverture(
    selectedValue,
    dateOuvertureGlobal,
    chartDetails?.valorisation?.dateEncours
  );

  const filteredFamilies = data?.families?.filter(createFamilyFilter(selectedValue));

  const encours =
    selectedValue === 'touslescontrats' ? data?.valorisation?.encours : chartDetails?.valorisation?.encours;

  const familiesdispo = data?.families?.map((famille) => {
    return famille.famille;
  });

  const handleChipClick = (chipValue: (typeof dataButtonFiltre)[number]) => {
    setLineChartTimespan(chipValue.value);
    if (selectedValue === 'touslescontrats') {
      filterPerformanceDashboard({ lineChartTimespan: chipValue.value, setPerformance, setPlusMoinsValue });
    } else {
      filterPerformanceFamily({
        lineChartTimespan: chipValue.value,
        setPerformance: setPerformanceFamily,
        setPlusMoinsValue: setPlusMoinsValueFamily,
      });
    }
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, chipValue.tracking);
  };
  return (
    <div>
      {showCancelModal && selectedContract && (
        <ContentModalRenonciation
          nomProduit={selectedContract.nomProduit}
          placementId={selectedContract.placementId}
          delayDays={selectedContract.cancelablePeriod ?? 30}
          onClose={handleCloseCancelModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: getGetApiDashboardContratsQueryKey() });
            handleCloseCancelModal();
          }}
        />
      )}

      {(isPending || isLoading) && <Loader />}
      <div className={styles.contractsContent}>
        <div className={styles.contractsInformation}>
          {windowWidth < 600 && <h1>{t('dashboard.contracts_other')}</h1>}

          <div className={styles.walletInfoHeader}>
            {windowWidth > 599 ? (
              <h1>{t('dashboard.contracts_other')}</h1>
            ) : (
              <span className={styles.wallet}>{t('common.myWallet')}</span>
            )}
            {(data?.families?.length ?? 0) > 1 && (
              <div>
                <DropDownContrats options={options} onChange={handleDropdownChange} families={familiesdispo} />
              </div>
            )}
          </div>

          <div className={styles.walletValue}>
            <span className={styles.value}>
              {encours == null ? '--' : t('format.currencyFlexible', { value: encours })}
              {isDemo && <DemoTag />}
            </span>
            <p className={styles.walletDate}>
              {windowWidth > 599 && <span>{t('common.myWallet')} </span>}
              au {dateOuverture}
            </p>
          </div>
          {shouldShowChart(selectedValue, data?.families) && (
            <>
              {!shouldHideTrendingValue(selectedValue, data?.families) && (
                <TrendingValue
                  value={selectedValue === 'touslescontrats' ? plusMoinsValue : plusMoinsValueFamily}
                  variant="dashboard"
                  secondaryValue={selectedValue === 'touslescontrats' ? performance : performanceFamily}
                  icon={selectedValue === 'touslescontrats' ? plusMoinsValue !== null : plusMoinsValueFamily !== null}
                />
              )}

              <div className={styles.chartContent}>
                <LineChart
                  timespan={lineChartTimespan}
                  donnees={
                    chartDetails && selectedValue !== 'touslescontrats'
                      ? (chartDetails.chartData ?? [])
                      : (data?.chartData ?? [])
                  }
                />

                <div className={styles.contentButtonFilter}>
                  {dataButtonFiltre.map((value, index, buttonsFiltre) => (
                    <ChipsRadioSmall
                      label={windowWidth < 600 ? value.textMobile : value.text}
                      key={value.value}
                      name="lineChartButtons"
                      value={value.value}
                      defaultCheck={index + 1 === buttonsFiltre.length}
                      onClick={() => handleChipClick(value)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {FEATURE_FLAGS.ARBITRAGE && <ArbitrageSlider className={styles.proposalSliderWrapper} showTitle />}

        {filteredFamilies?.map((family, index) => {
          return (
            <div className={styles.cardsContent} key={family.famille || index}>
              <div>
                <h2>
                  {getFamilyDisplayName(family?.famille ?? '')}
                  {isDemo && <DemoTag />}
                </h2>
              </div>
              <MtxGrid className={styles.familyContracts} row>
                {family?.listContrat?.enTraitement?.map((contract, index) => {
                  return (
                    <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4} key={contract.placementId || index}>
                      {contract.famille && contract.nomProduit && (
                        <CardInProgress
                          famille={contract.famille}
                          titreProduit={contract.nomProduit}
                          iconAssurance={contract.gestionnaireLogo ?? ''}
                          cancelablePeriod={contract.cancelablePeriod ?? 30}
                          onRequestCancellation={getCancellationHandler({
                            ...contract,
                            isCancelable: true,
                          })}
                        />
                      )}
                    </MtxGrid>
                  );
                })}
                {family?.listContrat?.enCours?.map((contract) => (
                  <ContractGridCard
                    key={contract.placementId}
                    contract={contract}
                    onRequestCancellation={getCancellationHandler(contract)}
                  />
                ))}{' '}
              </MtxGrid>
            </div>
          );
        })}

        <div className={styles.subscribeNewContract}>
          <MtxGrid className={styles.familyContracts} row>
            <MtxGrid
              col={4}
              colXs={12}
              colSm={6}
              colMd={6}
              colLg={4}
              colXl={4}
              offset={4}
              offsetXs={0}
              offsetSm={3}
              offsetMd={3}
              offsetLg={4}
              offsetXl={4}
            >
              <MtxButton
                className={styles.subscribeNewContractButton}
                onClick={() => {
                  window.open(URLS.MTP_URL_INVESTMENT, '_blank', 'noopener');
                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                    event_id: 'clic',
                    event_categorie: 'clic',
                    event_metier: 'espace client placement',
                    event_nom: 'souscrire un nouveau contrat',
                    event_detail_1: 'cta',
                  });
                }}
                variant="primary"
                size="large"
                fullWidth={true}
              >
                <div>{t('common.subscribeContract')}</div>
              </MtxButton>
            </MtxGrid>
          </MtxGrid>
        </div>
      </div>
    </div>
  );
};
